import { useEffect } from 'react';
import omit from 'lodash/omit';
import isUndefined from 'lodash/isUndefined';
import Catalyst from '@Services/Catalyst';
import { getLinkLocation, getLinkIndex } from '@Components/Results/analytics/citationLinksTracking';
import ResultTracking from '@Services/ResultTracking';
import PropTypes from 'prop-types';

/* high-level manager for handling delegated click events;handles only those which target has `data-click-delegation` attribute;
handles tracking if target has 'data-tracking` attribute value set to `true` */

function ContentClickManager({ children }) {
  const handleTracking = (data, href) => {
    const {
      trackingContext: context,
      trackingPreview: preview,
      trackingType: type,
      trackingAction: action,
      trackingLocation: location,
      trackingId: id,
      trackingCitationType: citationType,
      trackingName: citationName,
      trackingAuthorNames: authorNames,
      trackingDocLink: docLink,
      trackingTitle: title,
      trackingPublicationType: publicationType,
      trackingOnlineDate: onlineDate,
      trackingPublicationDate: publicationDate,
      trackingIndexTerms: indexTerms,
      trackingIssueNumber: issueNumber,
      trackingVolumeNumber: volumeNumber,
    } = data;

    const { trackingLinkLocation: nocontentLinkLocation, trackingLinkName: nocontentLinkName, trackingLinkType: nocontentLinkType } = data;

    const content = omit(
      {
        id,
        citationType,
        citationName,
        authorNames,
        docLink,
        title,
        publicationType,
        onlineDate,
        publicationDate,
        indexTerms,
        issueNumber,
        volumeNumber,
      },
      isUndefined
    );

    if (typeof type === 'string') {
      type.split(',').forEach((splitType) => {
        const trimmedType = splitType.trim();
        const linkUrl = href || data.link || content?.docLink;
        switch (trimmedType) {
          case 'linkout':
            if (data?.linkInResults) {
              ResultTracking.linkOut(context, content, linkUrl, preview, location, action, { link: data.linkInResults });
            } else {
              ResultTracking.linkOut(context, content, linkUrl, preview, location, action);
            }
            break;
          case 'linkout-nocontent':
            Catalyst.trackLinkOut(null, {
              location: nocontentLinkLocation,
              name: nocontentLinkName,
              type: nocontentLinkType,
            });
            break;
          case 'contentview':
            if (data?.linkInResults) {
              ResultTracking.contentView(context, content, preview, location, action, { link: data.linkInResults });
            } else {
              ResultTracking.contentView(context, content, preview, location, action);
            }
            break;
          case 'contentlinkclick':
            if (data?.linkInResults) {
              ResultTracking.trackContentLinkClick(data.linkInResults?.name, data.linkInResults?.location);
            } else {
              ResultTracking.trackContentLinkClick(action);
            }
            break;
          case 'structurecontentinteraction':
            ResultTracking.structureContentInteraction(action, context);
            break;
          case 'productfeatureused':
            ResultTracking.productFeatureUsed(action, context);
            break;
          case 'conversiondriverclick':
            Catalyst.trackConversionDriverClick(action);
            break;
          default:
            Logger.warn(`Unsupported tracking event type: ${splitType}`);
        }
      });
    }
  };
  useEffect(() => {
    const handleClick = (event) => {
      const target = event?.target?.closest('[data-click-delegation]');
      if (target) {
        const data = { ...target.dataset };
        const href = target.getAttribute('href');

        const trackingAttr = target.getAttribute('data-tracking-attr') || 'data-tracking';
        const isTracking = target.getAttribute(trackingAttr) === 'true';

        if (target.hasAttribute('data-tracking-action-text-content')) {
          data.trackingAction = target.textContent;
        }

        const {
          trackingTrackLinkLocationInResults: trackLinkLocationInResults,
          trackingAction: action,
          trackingContext: context,
          trackingFromRt: fromRT,
        } = data;

        if (trackLinkLocationInResults) {
          const linkLocation = getLinkLocation(target, { context, fromRT });
          const linkIndex = getLinkIndex(target);
          Object.assign(data, {
            linkInResults: {
              location: `${linkLocation}:${linkIndex}`,
              name: action,
            },
          });
        }

        if (isTracking) {
          handleTracking(data, href);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return children;
}

ContentClickManager.propTypes = {
  children: PropTypes.node,
};

export default ContentClickManager;
